import Typography from '@mui/material/Typography';
import {formatCopy} from 'utils/typography';
import React from "react";
import { useTranslation } from "react-i18next";
import {useFormContext, Controller} from "react-hook-form";

import TextField from "@mui/material/TextField";
import {LinkPopover} from '@vedmarket/ui-kit';

import {checkDecimalsAfterDot} from '../../../../../../../../../../../../../utils/validation'
import {ADORNMENTS} from "../../../../../../../../../../../../../config/constants";
import {useFormattedDecimalInput} from '../../../../../../../../../../../../../utils/hooks';
import {useDemoPackagingPlaybackComplete} from 'components/cabinet/demo/steps/packaging/use-packaging';

import { PACKAGE_FORM, PackageForm } from '../add_form/helpers';


const PackageItem = () => {

  const { t } = useTranslation()

  const { getValues, trigger, control } = useFormContext<PackageForm>()

  const isDemoPlaybackComplete = useDemoPackagingPlaybackComplete()

  const decimalInput = useFormattedDecimalInput();

  const errorValue = t('errors__message__incorrectValueTwoDecimalsAfterComma');

  const checkGrossWeightCartoonValueIsCorrect = (value: number) => {
    const netValue = getValues()[PACKAGE_FORM.NET_WEIGHT_CARTOON]

    const hasTwoDecimalsError = checkDecimalsAfterDot(value, errorValue);

    if (hasTwoDecimalsError !== true) {
      return hasTwoDecimalsError
    }

    return Number(value) > Number(netValue) ? true: t('errors__message__incorrectValueGrossWeight');

  }

  return <>
    <div className='add-product__row'>
      <Controller
        name={PACKAGE_FORM.LENGTH}
        control={control}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorValue),
          min: { value: 0, message: t('errors__message__incorrectValue') },
        }}
        render={({ field, fieldState }) => (
          <TextField
            className={`add-product__${PACKAGE_FORM.LENGTH}`}
            value={decimalInput.getDisplayValue({
              fieldName: PACKAGE_FORM.LENGTH,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(PACKAGE_FORM.LENGTH);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(PACKAGE_FORM.LENGTH);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={t('calc__text__packaging__lengthOfPackage')}
            InputLabelProps={{ shrink: true }}
            disabled={isDemoPlaybackComplete}
            InputProps={{ endAdornment: ADORNMENTS.SIZE }}
          />
        )}
      />
      <Controller
        name={PACKAGE_FORM.WIDTH}
        control={control}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorValue),
          min: { value: 0, message: t('errors__message__incorrectValue') },
        }}
        render={({ field, fieldState }) => (
          <TextField
            className={`add-product__${PACKAGE_FORM.WIDTH}`}
            value={decimalInput.getDisplayValue({
              fieldName: PACKAGE_FORM.WIDTH,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(PACKAGE_FORM.WIDTH);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(PACKAGE_FORM.WIDTH);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={t('calc__text__packaging__widthOfPackage')}
            InputLabelProps={{ shrink: true }}
            disabled={isDemoPlaybackComplete}
            InputProps={{ endAdornment: ADORNMENTS.SIZE }}
          />
        )}
      />
      <Controller
        name={PACKAGE_FORM.HEIGHT}
        control={control}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorValue),
          min: { value: 0, message: t('errors__message__incorrectValue') },
        }}
        render={({ field, fieldState }) => (
          <TextField
            className={`add-product__${PACKAGE_FORM.HEIGHT}`}
            value={decimalInput.getDisplayValue({
              fieldName: PACKAGE_FORM.HEIGHT,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(PACKAGE_FORM.HEIGHT);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(PACKAGE_FORM.HEIGHT);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={t('calc__text__packaging__heightOfPackage')}
            InputLabelProps={{ shrink: true }}
            disabled={isDemoPlaybackComplete}
            InputProps={{ endAdornment: ADORNMENTS.SIZE }}
          />
        )}
      />
      <div className='product-fields-help'>
        <LinkPopover
          linkText={t('calc__help__packageDimensionsLink')}
          text={formatCopy(t('calc__help__packageDimensions'))}
        />
      </div>
    </div>
    <div className='product-field product-field_package-weights'>
      <div className='product-field__pair product-field__pair_package-weights'>
        <div className='product-field product-field_net-weight'>
          <Controller
            name={PACKAGE_FORM.NET_WEIGHT_CARTOON}
            control={control}
            rules={{
              required: true,
              validate: (value) => checkDecimalsAfterDot(value, errorValue),
              min: { value: 0, message: t('errors__message__incorrectValue') },
            }}
            render={({ field, fieldState }) => (
              <TextField
                className={`add-product__${PACKAGE_FORM.NET_WEIGHT_CARTOON}`}
                value={decimalInput.getDisplayValue({
                  fieldName: PACKAGE_FORM.NET_WEIGHT_CARTOON,
                  rawValue: field.value,
                })}
                onChange={(e) => {
                  decimalInput.startEditing(PACKAGE_FORM.NET_WEIGHT_CARTOON);
                  const value = e.target.value;
                  const valueWithDot = value.replace(',', '.');
                  field.onChange(valueWithDot);
                }}
                onBlur={async () => {
                  field.onBlur();
                  decimalInput.stopEditing(PACKAGE_FORM.NET_WEIGHT_CARTOON);
                  if (getValues(PACKAGE_FORM.GROSS_WEIGHT_CARTOON) && getValues(PACKAGE_FORM.NET_WEIGHT_CARTOON)) {
                    await trigger([ PACKAGE_FORM.GROSS_WEIGHT_CARTOON ])
                  }
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                variant='outlined'
                label={t('common__text__netWeightPackaging')}
                InputLabelProps={{ shrink: true }}
                disabled={isDemoPlaybackComplete}
                InputProps={{ endAdornment: ADORNMENTS.WEIGHT }}
              />
            )}
          />
        </div>
        <div className='product-field product-field_gross-weight'>
          <Controller
            name={PACKAGE_FORM.GROSS_WEIGHT_CARTOON}
            control={control}
            rules={{
              required: true,
              validate: (value: number) => checkGrossWeightCartoonValueIsCorrect(value),
              min: { value: 0, message: t('errors__message__incorrectValue') },
            }}
            render={({ field, fieldState }) => (
              <TextField
                className={`add-product__${PACKAGE_FORM.GROSS_WEIGHT_CARTOON}`}
                value={decimalInput.getDisplayValue({
                  fieldName: PACKAGE_FORM.GROSS_WEIGHT_CARTOON,
                  rawValue: field.value,
                })}
                onChange={(e) => {
                  decimalInput.startEditing(PACKAGE_FORM.GROSS_WEIGHT_CARTOON);
                  const value = e.target.value;
                  const valueWithDot = value.replace(',', '.');
                  field.onChange(valueWithDot);
                }}
                onBlur={() => {
                  field.onBlur();
                  decimalInput.stopEditing(PACKAGE_FORM.GROSS_WEIGHT_CARTOON);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                variant='outlined'
                label={t('common__text__grossPackagingWeight')}
                InputLabelProps={{ shrink: true }}
                disabled={isDemoPlaybackComplete}
                InputProps={{ endAdornment: ADORNMENTS.WEIGHT }}
              />
            )}
          />
        </div>
      </div>
      <LinkPopover
        linkText={t('calc__help__packageWeightsLink')}
        text={formatCopy(t('calc__help__packageWeights'))}
      />
    </div>
  </>
}

export default PackageItem
